import { apiOrganization } from "@/api/organization.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { preboardingKeys } from "@/queries/preboarding/use-preboarding.query.ts";
import { useThemeStore } from "@/stores/theme.store.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiOrganizationBackgroundApplyTo,
  ApiOrganizationGeneralSettings,
  ApiOrganizationPayload,
  ApiOrganizationSettingsHomeLayout,
  ApiOrganizationSettingsHomeLayoutPayload,
  ApiOrganizationSettingsMetricCalculations,
  ApiOrganizationSettingsViewFilter,
} from "@/types/organization.types.ts";
import { ApiOrganizationSettingsPreboarding } from "@/types/preboarding.types.ts";
import { ApiFeatureSettingsPayload } from "@/types/subscription.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const organizationKeys = createQueryKeys("organization", {
  settings: null,
  featureSettings: null,
  viewFilter: null,
  metricCalculations: null,
  preboarding: null,
  homeLayout: null,
});

export const useOrganizationSettings = (enabled: boolean) => {
  return useQuery({
    ...organizationKeys.settings,
    queryFn: async () => {
      const result = await apiOrganization.getSettings();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled,
  });
};

export const useOrganizationUpdateSettings = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues>
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  const { setTheme } = useThemeStore();

  return useMutation({
    ...organizationKeys.settings,
    mutationFn: async (settings: ApiOrganizationPayload) => {
      const result = await apiOrganization.updateSettings(settings);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (newSettings: ApiOrganizationGeneralSettings) => {
      setTheme(newSettings.theme ?? "default");
      queryClient.setQueryData(organizationKeys.settings.queryKey, newSettings);
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useOrganizationSetLogo = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    ...organizationKeys.settings,
    mutationFn: async (fileUUID: string) => {
      const result = await apiOrganization.setLogo(fileUUID);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (newSettings) => {
      queryClient.setQueryData(organizationKeys.settings.queryKey, newSettings);
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.me.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useOrganizationDeleteLogo = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    ...organizationKeys.settings,
    mutationFn: async () => {
      const result = await apiOrganization.deleteLogo();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (newSettings) => {
      queryClient.setQueryData(organizationKeys.settings.queryKey, newSettings);
      void queryClient.invalidateQueries({
        queryKey: employeesKeys.me.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useOrganizationSetBackground = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    ...organizationKeys.settings,
    mutationFn: async ({
      fileUUID,
      applyTo = ApiOrganizationBackgroundApplyTo.new_employees,
    }: {
      fileUUID: string;
      applyTo?: ApiOrganizationBackgroundApplyTo;
    }) => {
      const result = await apiOrganization.setBackground(fileUUID, applyTo);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (newSettings, { applyTo = ApiOrganizationBackgroundApplyTo.new_employees }) => {
      queryClient.setQueryData(organizationKeys.settings.queryKey, newSettings);

      if (applyTo !== ApiOrganizationBackgroundApplyTo.new_employees) {
        void queryClient.invalidateQueries({ queryKey: employeesKeys.detail._def });
        void queryClient.invalidateQueries({ queryKey: employeesKeys.me.queryKey });
      }

      void queryClient.invalidateQueries({
        queryKey: organizationKeys.settings.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useOrganizationDeleteBackground = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    ...organizationKeys.settings,
    mutationFn: async () => {
      const result = await apiOrganization.deleteBackground();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (newSettings) => {
      queryClient.setQueryData(organizationKeys.settings.queryKey, newSettings);

      void queryClient.invalidateQueries({
        queryKey: organizationKeys.settings.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useOrganizationFeaturesSettings = () => {
  return useQuery({
    ...organizationKeys.featureSettings,
    queryFn: async () => {
      const result = await apiOrganization.getFeatures();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useOrganizationUpdateFeaturesSettings = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues>
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    ...organizationKeys.settings,
    mutationFn: async (settings: ApiFeatureSettingsPayload) => {
      const { success, error } = await apiOrganization.updateFeatures(settings);

      if (error || !success) throw new ApiErrorException(error);

      return success;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationKeys.featureSettings.queryKey });
      void queryClient.invalidateQueries({ queryKey: employeesKeys.me.queryKey });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
export const useOrganizationViewFilter = (enabled: boolean = true) => {
  return useQuery({
    ...organizationKeys.viewFilter,
    enabled: enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const result = await apiOrganization.getViewFilter();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useOrganizationUpdateViewFilter = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues>
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    ...organizationKeys.viewFilter,
    mutationFn: async (settings: ApiOrganizationSettingsViewFilter) => {
      const { success, error } = await apiOrganization.updateViewFilter(settings);

      if (error || !success) throw new ApiErrorException(error);

      return success;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationKeys.viewFilter.queryKey });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useOrganizationMetricCalculations = (enabled: boolean = true) => {
  return useQuery({
    ...organizationKeys.metricCalculations,
    enabled: enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const result = await apiOrganization.getMetricCalculations();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useOrganizationUpdateMetricCalculations = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues>
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    ...organizationKeys.metricCalculations,
    mutationFn: async (settings: ApiOrganizationSettingsMetricCalculations) => {
      const result = await apiOrganization.updateMetricCalculations(settings);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: organizationKeys.metricCalculations.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useOrganizationPreboardingSettings = (enabled: boolean = true) => {
  return useQuery({
    ...organizationKeys.preboarding,
    enabled: enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const result = await apiOrganization.getPreboardingSettings();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useOrganizationUpdatePreboardingSettings = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues>
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (settings: ApiOrganizationSettingsPreboarding) => {
      const result = await apiOrganization.updatePreboardingSettings(settings);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (settings: ApiOrganizationSettingsPreboarding) => {
      queryClient.setQueryData(organizationKeys.preboarding.queryKey, settings);
      void queryClient.invalidateQueries({ queryKey: preboardingKeys.home.queryKey });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useOrganizationHomeLayoutSettings = (enabled: boolean = true) => {
  return useQuery({
    ...organizationKeys.homeLayout,
    enabled: enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const result = await apiOrganization.getHomeLayoutSettings();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useOrganizationUpdateHomeLayoutSettings = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues>
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (settings: ApiOrganizationSettingsHomeLayoutPayload) => {
      const result = await apiOrganization.updateHomeLayoutSettings(settings);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (settings: ApiOrganizationSettingsHomeLayout) => {
      queryClient.setQueryData(organizationKeys.homeLayout.queryKey, settings);
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
